<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { CheckCircle2, FileUp, UploadCloud } from 'lucide-vue-next'
import { computed, reactive, ref } from 'vue'

import DocumentUploadCard from '@/components/applications/DocumentUploadCard.vue'
import { Button } from '@/components/ui/button'
import { MAX_FILE_SIZE_MB } from '@/lib/constants'
import { documentUploadSubmitPath } from '@/routes'

interface Document {
  id: number
  name: string
  status: string
  rejection_reason: string | null
  file_name: string | null
}

interface Props {
  token: string
  documents: Document[]
  has_bank_statement_request: boolean
  bank_statement_url: string
}

const props = defineProps<Props>()

// ── File selection & validation ──────────────────────────────────────────────

const selectedFiles = reactive<Record<number, File | null>>({})
const fileErrors = reactive<Record<number, string | null>>({})
const processing = ref(false)
const uploadedSuccessfully = ref(false)

function onFileSelect(docId: number, file: File) {
  selectedFiles[docId] = file
  fileErrors[docId] = null
}

function onFileError(docId: number, message: string) {
  selectedFiles[docId] = null
  fileErrors[docId] = message
}

function onFileClear(docId: number) {
  selectedFiles[docId] = null
  fileErrors[docId] = null
}

const selectedCount = computed(() => props.documents.filter((doc) => selectedFiles[doc.id]).length)

const hasAllFiles = computed(() =>
  props.documents.length > 0 && props.documents.every((doc) => selectedFiles[doc.id]),
)

const hasAnyError = computed(() =>
  Object.values(fileErrors).some((e) => e !== null && e !== undefined),
)

// ── Bulk submit ─────────────────────────────────────────────────────────────

function closeTab() {
  if (window.opener) {
    window.opener.location.reload()
  }
  window.close()
}

function submitAll() {
  if (!hasAllFiles.value || hasAnyError.value || processing.value) return
  processing.value = true

  const uploads = props.documents
    .filter((doc) => selectedFiles[doc.id])
    .map((doc) => ({ name: doc.name, file: selectedFiles[doc.id] as File }))

  router.post(
    documentUploadSubmitPath(props.token),
    { uploads },
    {
      preserveScroll: true,
      onSuccess: () => {
        uploadedSuccessfully.value = true
      },
      onFinish: () => {
        processing.value = false
        props.documents.forEach((doc) => { selectedFiles[doc.id] = null })
      },
    },
  )
}
</script>

<template>
  <div class="min-h-screen bg-background px-4 py-10 text-foreground">
    <div class="mx-auto max-w-2xl space-y-5">

      <!-- Header -->
      <div class="text-center">
        <div class="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-primary/10">
          <UploadCloud class="size-7 text-primary" />
        </div>
        <h1 class="text-2xl font-bold">Upload your documents</h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Accepted: PDF, JPEG, PNG — max {{ MAX_FILE_SIZE_MB }} MB each.
        </p>
      </div>

      <!-- Progress bar -->
      <div v-if="documents.length > 0 && !uploadedSuccessfully" class="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div class="mb-2 flex items-center justify-between text-sm font-medium">
          <span class="text-foreground">Progress</span>
          <span :class="hasAllFiles ? 'text-emerald-500 dark:text-emerald-400' : 'text-amber-500 dark:text-amber-400'">
            {{ selectedCount }} of {{ documents.length }} ready
          </span>
        </div>
        <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            class="h-full rounded-full transition-all duration-500"
            :class="hasAllFiles ? 'bg-emerald-500' : 'bg-amber-400'"
            :style="{ width: documents.length ? `${(selectedCount / documents.length) * 100}%` : '0%' }"
          />
        </div>
      </div>

      <!-- Success state -->
      <div v-if="uploadedSuccessfully" class="rounded-xl border border-emerald-300 bg-emerald-50 p-6 text-center dark:border-emerald-800 dark:bg-emerald-950/40">
        <CheckCircle2 class="mx-auto mb-3 size-11 text-emerald-500" />
        <h2 class="text-lg font-semibold text-emerald-800 dark:text-emerald-300">All documents uploaded!</h2>
        <p class="mt-1 text-sm text-emerald-700 dark:text-emerald-400">Your documents have been received successfully.</p>
        <Button class="mt-4 gap-2 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600" @click="closeTab">
          <CheckCircle2 class="size-4" />
          Close this page
        </Button>
      </div>

      <!-- Document cards -->
      <div v-if="!uploadedSuccessfully" class="space-y-2">
        <DocumentUploadCard
          v-for="doc in documents"
          :key="doc.id"
          :doc="doc"
          :selected-file="selectedFiles[doc.id] ?? null"
          :error="fileErrors[doc.id] ?? null"
          shadow
          @select="(file) => onFileSelect(doc.id, file)"
          @error="(message) => onFileError(doc.id, message)"
          @clear="onFileClear(doc.id)"
        />

        <p v-if="documents.length === 0" class="py-8 text-center text-sm text-muted-foreground">
          No documents are currently requested.
        </p>
      </div>

      <!-- Submit button -->
      <Button
        v-if="documents.length > 0 && !uploadedSuccessfully"
        class="w-full gap-2 py-5 text-base font-semibold"
        :disabled="!hasAllFiles || hasAnyError || processing"
        @click="submitAll"
      >
        <FileUp class="size-4" />
        <span v-if="processing">Uploading…</span>
        <span v-else-if="hasAllFiles">Upload {{ documents.length }} document{{ documents.length !== 1 ? 's' : '' }}</span>
        <span v-else>Select all documents to continue ({{ selectedCount }}/{{ documents.length }})</span>
      </Button>

      <!-- Bank statement note -->
      <div
        v-if="has_bank_statement_request"
        class="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-center text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300"
      >
        Please also upload your bank statements securely at
        <a
          :href="bank_statement_url"
          target="_blank"
          class="font-medium underline hover:opacity-80"
        >bankstatements.com.au</a>
        and follow the prompts. For assistance call <strong>1300 045 047</strong>.
      </div>

    </div>
  </div>
</template>
